import React from 'react';
import { Page, Text, View, Document, StyleSheet, PDFViewer, PDFDownloadLink, Font, Image } from '@react-pdf/renderer';
import { BrowserView, MobileView } from 'react-device-detect';
import PdfPreview from 'ui-component/PdfPreview';
import { Stack, Typography } from '@mui/material';
import phetsarath_ot from 'assets/fonts/phetsarath_ot.ttf';
import logo from 'assets/images/Logo-Aone-Final-Full.png';
import moment from 'moment';
import { ToWords } from 'to-words';

Font.register({ family: 'Noto Sans Lao', src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@master/hinted/ttf/NotoSansLao/NotoSansLao-Regular.ttf' });

const toWords = new ToWords({
    localeCode: 'en-US',
    converterOptions: { currency: false, ignoreDecimal: false, ignoreZeroCurrency: false, doNotAddOnly: false }
});

const formatCurrency = (num) => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    return `₭${Number(num).toLocaleString('en-US')}`;
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
    body: { paddingTop: 30, paddingBottom: 60, paddingHorizontal: 35, fontFamily: 'Noto Sans Lao' },
    title: { padding: 5, fontSize: 20, textAlign: 'center', fontWeight: 'bold' },
    section: { flexDirection: 'row', alignItems: 'center', fontSize: 9, marginBottom: 2 },
    label: { width: 80, fontWeight: 'bold' },
    value: { flex: 1 },
    logo: { position: 'absolute', top: 20, left: 35, width: 80 },
    stamps: { position: 'absolute', width: 300, opacity: 0.3, top: '40%', left: '30%' },
    table: { width: '100%', marginTop: 10 },
    rowTable: { flexDirection: 'row', fontSize: 9 },
    headerTable: { backgroundColor: '#f8f9fa', fontWeight: 'bold', borderTopWidth: 1, borderTopColor: '#000', borderTopStyle: 'solid' },
    // ── Column widths (UNCHANGED) ─────────────────────────────────────────────
    row0: { width: '25%', border: '1px solid #000', borderRight: 'none', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row1: { width: '30%', border: '1px solid #000', borderRight: 'none', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row2: { width: '15%', border: '1px solid #000', borderRight: 'none', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row3: { width: '10%', border: '1px solid #000', borderRight: 'none', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' },
    row4: { width: '20%', border: '1px solid #000', paddingTop: 8, paddingBottom: 8, paddingLeft: 4, fontSize: 10, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden' }
});

// ─── Document ─────────────────────────────────────────────────────────────────
const MyDocument = ({ data }) => {
    return (
        <Document>
            <Page size="A4" style={styles.body} wrap>
                <Image style={styles.logo} src={logo} />
                <Text style={styles.title}>Credit Invoice Pending</Text>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 15, marginBottom: 5 }}>
                    <View style={{ width: '55%' }}>
                        <View style={styles.section}><Text style={styles.label}>Customer no.</Text><Text style={styles.value}>: {data?.customerID}</Text></View>
                        <View style={styles.section}><Text style={styles.label}>Mobile</Text><Text style={styles.value}>: {data?.mobile}</Text></View>
                        <View style={styles.section}><Text style={styles.label}>Customer</Text><Text style={styles.value}>: {data?.customerName}</Text></View>
                        <View style={styles.section}><Text style={styles.label}>Address</Text><Text style={styles.value}>: {`${data?.villages?.vill_name}, ${data?.districts?.dr_name}, ${data?.provinces?.pr_name}`}</Text></View>
                    </View>
                    <View style={{ width: '40%' }}>
                        <View style={styles.section}><Text style={styles.label}>Date</Text><Text style={styles.value}>: {moment(new Date()).format('DD-MM-yyyy HH:mm')}</Text></View>
                        <View style={styles.section}><Text style={styles.label}>Term Limit</Text><Text style={[styles.value, { color: 'green' }]}>: {data?.creditOverduePayment}</Text></View>
                        <View style={styles.section}><Text style={styles.label}>Credit Limit</Text><Text style={[styles.value, { color: '#f57224' }]}>: {formatCurrency(data?.creditLimit)}</Text></View>
                    </View>
                </View>
                {/* ── Table (UNCHANGED structure) ── */}
                <View style={styles.table}>
                    <View style={[styles.rowTable, styles.headerTable]} fixed>
                        <Text style={styles.row0}>InvoiceNumber</Text>
                        <Text style={styles.row1}>Customer Name</Text>
                        <Text style={styles.row2}>Invoice Date</Text>
                        <Text style={styles.row3}>Term(Day)</Text>
                        <Text style={styles.row4}>Credit</Text>
                    </View>
                    {data?.creditinvoices?.pendingInvoice?.map((item, i) => (
                        <View key={i} style={styles.rowTable} wrap={false}>
                            <Text style={[styles.row0, { fontSize: 10 }]}>{item?.invoiceNumber}</Text>
                            <Text style={[styles.row1, { fontSize: 8 }]}>{data?.customerName}</Text>
                            <Text style={[styles.row2, { fontSize: 10 }]}>{moment(new Date(item?.invoiceDate)).format('DD-MM-yyyy')}</Text>
                            <Text style={[styles.row3, { fontSize: 10 }]}>{`${item?.daysCount}`}</Text>
                            <Text style={[styles.row4, { fontSize: 10, color: '#f57224' }]}>{formatCurrency(item?.totalCredit)}</Text>
                        </View>
                    ))}
                    {/* Footer: Total row */}
                    <View style={styles.rowTable} wrap={false}>
                        <View style={{ width: '55%', border: '1px solid #000', borderRight: 'none', padding: 6, justifyContent: 'center' }}>
                            <Text style={{ fontSize: 8, textAlign: 'center' }}>{toWords.convert(data?.creditinvoices?.totalCredit || 0)}</Text>
                        </View>
                        <View style={{ width: '25%', border: '1px solid #000', borderRight: 'none', padding: 6, justifyContent: 'center' }}>
                            <Text style={{ fontSize: 9, fontWeight: 'bold' }}>Total Credit</Text>
                        </View>
                        <View style={{ width: '20%', border: '1px solid #000', padding: 6, justifyContent: 'center' }}>
                            <Text style={{ fontSize: 9, color: '#f57224', textAlign: 'right' }}>{formatCurrency(data?.creditinvoices?.totalCredit)}</Text>
                        </View>
                    </View>
                </View>
                {/* ── Signature Section ── */}
                <View style={{ marginTop: 25, flexDirection: 'row', justifyContent: 'space-between' }} wrap={false}>
                    <View style={{ width: '48%' }}>
                        <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Print By: {data?.printBy || '....................'}</Text>
                        <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                    </View>
                    <View style={{ width: '48%' }}>
                        <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Approve By: {data?.approveBy || '....................'}</Text>
                        <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                    </View>
                </View>
                {/* ── Company Info ── */}
                <View style={{ marginTop: 15, borderWidth: 1, borderColor: 'blue', borderRadius: 10, padding: 10 }} wrap={false}>
                    <Text style={{ color: 'blue', fontSize: 8, fontWeight: 'bold' }}>A One Distributors Co., Ltd Nongbone Road, Naxay Village, Xaysettha District, Vientiane Lao PDR Mobile: 020 88871111</Text>
                </View>
            </Page>
        </Document>
    );
};

// ─── Component ────────────────────────────────────────────────────────────────
const PendingCreditInvoicePDF = ({ data }) => {
    if (!data) return null;
    return (
        <>
            <PdfPreview document={<MyDocument data={data} />} fileName={data?.customerID} />
        </>
    );
};

export default PendingCreditInvoicePDF;
